import { FRAMEWORK_LABELS, FRAMEWORK_ORDER } from "../context/FrameworksContext";
import type { FrameworkId } from "../context/FrameworksContext";

import { cbPalette } from "../lib/colourBlind";
// ── Framework metadata ────────────────────────────────────────────────────
// The single source for framework labels, colours and sub-pages used by the
// navigation (client shell, consultant read-through, consultant and auditor
// framework dots). FW_COLOR elsewhere should import from here.

export interface FrameworkPage {
  /** Shell-relative client path. */
  to: string;
  label: string;
}

export interface FrameworkMeta {
  id: FrameworkId;
  label: string;
  color: string;
  /** Sub-pages for the framework group. PCI DSS lists the enrolled SAQs instead (see frameworkPages). */
  pages: FrameworkPage[];
}

export const FW_COLOR: Record<FrameworkId, string> = cbPalette({
  iso27001:              "#6366F1",
  cyber_essentials:      "#10B981",
  cyber_essentials_plus: "#10B981",
  iso42001:              "#8B5CF6",
  iso9001:               "#3B82F6",
  soc2:                  "#F59E0B",
  pci_dss:               "#EF4444",
  iasme_ca_sole_trader:  "#0ea5e9",
  iasme_ca_micro:        "#0284c7",
  iasme_ca_small:        "#0369a1",
  iasme_ca_medium_large: "#075985",
  nist_csf:              "#0d9488",
  gdpr:                  "#7c3aed",
  uk_gdpr:               "#6d28d9",
  iso45001:              "#16a34a",
  iso14001:              "#15803d",
  iwa48:                 "#065f46",
  ncsc_caf:              "#0f766e",
});

/** Alias kept for callers that used the old Sidebar name. */
export const FW_LABEL: Record<FrameworkId, string> = FRAMEWORK_LABELS;

const FRAMEWORK_PAGES: Record<Exclude<FrameworkId, "pci_dss">, FrameworkPage[]> = {
  iso27001: [
    { to: "/mandatory-clauses", label: "Required clauses" },
    { to: "/annex-a",           label: "Annex A controls" },
    { to: "/iso27001-soa",      label: "Statement of Applicability" },
  ],
  cyber_essentials: [
    { to: "/cyber-essentials", label: "Assessment" },
  ],
  cyber_essentials_plus: [
    { to: "/cyber-essentials-plus", label: "Assessment" },
  ],
  iso42001: [
    { to: "/iso42001",     label: "Clauses" },
    { to: "/iso42002",     label: "Annex A controls" },
    { to: "/iso42001-soa", label: "Statement of Applicability" },
  ],
  iso9001: [
    { to: "/iso9001",     label: "Clauses" },
    { to: "/iso9002",     label: "Guidelines" },
    { to: "/iso9001-soa", label: "Statement of Applicability" },
  ],
  soc2: [
    { to: "/soc2", label: "Trust services criteria" },
  ],
  iasme_ca_sole_trader:  [{ to: "/iasme-ca-sole-trader",  label: "Assessment" }],
  iasme_ca_micro:        [{ to: "/iasme-ca-micro",         label: "Assessment" }],
  iasme_ca_small:        [{ to: "/iasme-ca-small",         label: "Assessment" }],
  iasme_ca_medium_large: [{ to: "/iasme-ca-medium-large",  label: "Assessment" }],
  nist_csf:              [{ to: "/nist-csf",               label: "Reference guide" }],
  gdpr:    [{ to: "/gdpr", label: "GDPR Compliance" }],
  uk_gdpr: [{ to: "/gdpr", label: "UK GDPR Compliance" }],
  iso45001: [
    { to: "/iso45001",     label: "OH&S clauses" },
    { to: "/iso45001-soa", label: "Statement of Applicability" },
  ],
  iso14001: [
    { to: "/iso14001",     label: "EMS clauses" },
    { to: "/iso14001-soa", label: "Statement of Applicability" },
  ],
  iwa48: [
    { to: "/iwa48", label: "ESG principles" },
  ],
  // The same shape as the ISO standards: the assessment, then what applies.
  ncsc_caf: [
    { to: "/ncsc-caf",     label: "Objectives and outcomes" },
    { to: "/ncsc-caf-soa", label: "Statement of Applicability" },
  ],
};

export const PCI_SAQ_LABELS: Record<string, string> = {
  "saq-a":          "SAQ A",
  "saq-a-ep":       "SAQ A-EP",
  "saq-b":          "SAQ B",
  "saq-b-ip":       "SAQ B-IP",
  "saq-c":          "SAQ C",
  "saq-c-vt":       "SAQ C-VT",
  "saq-p2pe":       "SAQ P2PE",
  "saq-d-merchant": "SAQ D (Merchant)",
  "saq-d-sp":       "SAQ D (Service Provider)",
};

/** Canonical SAQ order for menus that list every type. */
export function pciSaqLabel(saqType: string): string {
  return PCI_SAQ_LABELS[saqType] ?? saqType.toUpperCase();
}

export function pciSaqPages(saqTypes: string[]): FrameworkPage[] {
  return saqTypes.map((t) => ({ to: `/pci-dss/${t}`, label: pciSaqLabel(t) }));
}

/** Sub-pages of a framework group. PCI DSS renders the enrolled SAQs. */
export function frameworkPages(id: FrameworkId, pciSaqTypes: string[] = []): FrameworkPage[] {
  if (id === "pci_dss") return pciSaqPages(pciSaqTypes);
  return FRAMEWORK_PAGES[id] ?? [];
}

export const FRAMEWORK_META: Record<FrameworkId, FrameworkMeta> = FRAMEWORK_ORDER.reduce((acc, id) => {
  acc[id] = { id, label: FRAMEWORK_LABELS[id], color: FW_COLOR[id], pages: frameworkPages(id) };
  return acc;
}, {} as Record<FrameworkId, FrameworkMeta>);

export function frameworkMeta(id: FrameworkId): FrameworkMeta {
  return FRAMEWORK_META[id];
}

/** Label for any framework id, tolerant of ids the frontend does not know. */
export function frameworkLabel(id: string): string {
  return (FRAMEWORK_LABELS as Record<string, string | undefined>)[id] ?? id;
}

/** Colour for any framework id; a neutral grey for unknown ids. */
export function frameworkColor(id: string): string {
  return (FW_COLOR as Record<string, string | undefined>)[id] ?? "#94a3b8";
}

/** Data protection laws live under "Data Protection", not "Frameworks". */
export const REGULATORY_FW_IDS: ReadonlySet<FrameworkId> = new Set<FrameworkId>(["gdpr", "uk_gdpr"]);

/** Environmental / ESG standards live under "Environment & Sustainability". */
export const ESG_FW_IDS: ReadonlySet<FrameworkId> = new Set<FrameworkId>(["iso14001", "iwa48"]);

export function isCertificationFramework(id: FrameworkId): boolean {
  return !REGULATORY_FW_IDS.has(id) && !ESG_FW_IDS.has(id);
}

/** The enrolled ids in canonical display order. */
export function orderedFrameworks(enrolled: Iterable<FrameworkId>): FrameworkId[] {
  const set = enrolled instanceof Set ? enrolled : new Set(enrolled);
  return FRAMEWORK_ORDER.filter((id) => set.has(id));
}
